import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui-kit/tabs';
import { usePermittedSidebar } from '@/hooks/use-permitted-sidebar';
import { ConfirmationModal } from '@/components/core/confirmation-modal/confirmation-modal';
import { useToast } from '@/hooks/use-toast';
import { formatDateOnly } from '@/lib/utils/custom-date/custom-date';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { cancelLeaveRequest } from '@/modules/leave/hr-leave-manager/services/leave-manager.service';
import { invalidateLeaveRequestQueries } from '@/modules/leave/leave-request-queries';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  useMarkMyProfileCompleted,
  useUpdateEmployeeBasicInfo,
  useUpdateEmployeeContactInfo,
  useUpdateEmployeeHrInfo,
  useUpdateMyBasicInfo,
  useUpdateMyContactInfo,
  useUpdateMySkills,
} from '../../hooks/use-employees';
import { useProfileCompletion } from '../../hooks/use-profile-completion';
import { skillsForQueryOptions } from '../../hooks/use-skill-lookups';
import { useSyncLeaveDetails } from '../../hooks/use-sync-leave-details';
import { basicInfoDefaults } from '../../schemas/basic-info-schema';
import { contactInfoDefaults } from '../../schemas/contact-info-schema';
import {
  contactSchema,
  ContactValues,
  contactValuesToInput,
  employeeToContactValues,
  employeeToHrInfoValues,
  employeeToOverviewValues,
  employeeToSkillsValues,
  hrInfoSchema,
  HrInfoValues,
  hrInfoValuesToTerms,
  isProfileCompleteAfterSelfSave,
  overviewSchemaFor,
  OverviewValues,
  overviewValuesToInput,
  savedSkillIdsOf,
  selfContactExtendedSchema,
  selfContactSchema,
  selfContactValuesToInput,
  selfOverviewExtendedSchema,
  selfOverviewSchema,
  selfOverviewValuesToInput,
  selfSkillsSchema,
  SelfSkillsValues,
  SkillsFormLookups,
  skillsValuesToInput,
} from '../../schemas/employee-form-schema';
import { createEmptyTerm } from '../../schemas/hr-term-schema';
import { LeaveDayChange } from '../../services/leave-day-changes';
import { getLeaveDayChanges } from '../../services/leave-day-changes.service';
import {
  createEmptySkillEntry,
  createEmptySubCategoryExperience,
} from '../../schemas/skills-schema';
import { Employee } from '../../types/employee.types';
import { requiredFieldsOf } from './required-fields';
import { BasicInfoStep } from './steps/basic-info-step';
import { ContactInfoStep } from './steps/contact-info-step';
import { EmploymentStep } from './steps/employment-step';
import { SkillsStep } from './steps/skills-step';
import { TabForm } from './tab-form';
import { useTranslatedResolver } from './translate-form-errors';

export interface EmployeeEditTabsProps {
  employee: Employee;
  /**
   * `admin` (default): HR edits an employee — Overview, Contact Info and HR Info. `self`: the person edits
   * their own profile — Overview (without the fields HR owns), Contact Info and Skills; it needs
   * `skillLookups` to match stored skills to today's ids.
   */
  mode?: 'admin' | 'self';
  skillLookups?: SkillsFormLookups;
}

interface TabProps {
  employee: Employee;
  isSelf: boolean;
}

/**
 * Self-service saves record whether the profile is now complete (see `useProfileCompletion`). A completed
 * profile stays completed; without extended info any own save completes it.
 */
const useCompletionFlag = (employee: Employee) => {
  const { requiresExtendedInfo } = useProfileCompletion();
  return (input: Record<string, unknown>) => ({
    ...input,
    IsProfileCompleted:
      employee.IsProfileCompleted === true ||
      isProfileCompleteAfterSelfSave({ ...employee, ...input }, requiresExtendedInfo),
  });
};

/** Once a self-service save completed the profile, lets the gate through and opens the first page they may use. */
const useFinishCompletion = () => {
  const { isCompletionRequired } = useProfileCompletion();
  const markProfileCompleted = useMarkMyProfileCompleted();
  const { landingPath } = usePermittedSidebar();
  const navigate = useNavigate();
  return (input: Record<string, unknown>) => {
    if (!isCompletionRequired || input.IsProfileCompleted !== true) return;
    markProfileCompleted();
    navigate(landingPath ?? '/', { replace: true });
  };
};

const OverviewTabForm = ({ employee, isSelf }: TabProps) => {
  const { requiresExtendedInfo, isCompletionRequired } = useProfileCompletion();
  const isExtendedSelf = isSelf && requiresExtendedInfo;
  const schema = useMemo(
    () =>
      isExtendedSelf
        ? selfOverviewExtendedSchema
        : isSelf
          ? selfOverviewSchema
          : overviewSchemaFor(employee.UserId),
    [isExtendedSelf, isSelf, employee.UserId]
  );
  const resolver = useTranslatedResolver<OverviewValues>(schema);
  const requiredFields = useMemo(() => requiredFieldsOf(schema, basicInfoDefaults), [schema]);
  const form = useForm<OverviewValues>({
    resolver,
    defaultValues: employeeToOverviewValues(employee),
  });
  const withCompletion = useCompletionFlag(employee);
  const finishCompletion = useFinishCompletion();
  const admin = useUpdateEmployeeBasicInfo();
  const self = useUpdateMyBasicInfo();

  const handleSave = async (values: OverviewValues) => {
    if (isSelf) {
      const input = withCompletion(selfOverviewValuesToInput(values));
      await self.mutateAsync({ employee, input });
      finishCompletion(input);
    } else {
      await admin.mutateAsync({ employee, input: overviewValuesToInput(values) });
    }
  };

  return (
    <TabForm
      form={form}
      onSave={handleSave}
      isSaving={admin.isPending || self.isPending}
      allowPristineSave={isSelf && isCompletionRequired}
      requiredFields={requiredFields}
    >
      <BasicInfoStep isEditMode isSelfService={isSelf} employeeUserId={employee.UserId} />
    </TabForm>
  );
};

const ContactTabForm = ({ employee, isSelf }: TabProps) => {
  const { requiresExtendedInfo, isCompletionRequired } = useProfileCompletion();
  const schema = isSelf
    ? requiresExtendedInfo
      ? selfContactExtendedSchema
      : selfContactSchema
    : contactSchema;
  const resolver = useTranslatedResolver<ContactValues>(schema);
  const requiredFields = useMemo(() => requiredFieldsOf(schema, contactInfoDefaults), [schema]);
  const form = useForm<ContactValues>({
    resolver,
    defaultValues: employeeToContactValues(employee),
  });
  const withCompletion = useCompletionFlag(employee);
  const finishCompletion = useFinishCompletion();
  const admin = useUpdateEmployeeContactInfo();
  const self = useUpdateMyContactInfo();

  const handleSave = async (values: ContactValues) => {
    if (isSelf) {
      const input = withCompletion(selfContactValuesToInput(values));
      await self.mutateAsync({ employee, input });
      finishCompletion(input);
    } else {
      await admin.mutateAsync({ employee, input: contactValuesToInput(values) });
    }
  };

  return (
    <TabForm
      form={form}
      onSave={handleSave}
      isSaving={admin.isPending || self.isPending}
      allowPristineSave={isSelf && isCompletionRequired}
      requiredFields={requiredFields}
    >
      <ContactInfoStep
        isSelfService={isSelf}
        requiresExtendedInfo={isSelf && requiresExtendedInfo}
      />
    </TabForm>
  );
};

/** The open leaves a work-day change recounts, one line each, for the confirmation before the save. */
const LeaveDayChangeList = ({ changes }: { changes: LeaveDayChange[] }) => {
  const { t } = useTranslation();
  return (
    <div className="space-y-2">
      <p>{t('LEAVE_DAYS_CHANGE_DESCRIPTION')}</p>
      <ul className="list-disc space-y-1 pl-5">
        {changes.map(({ leave, before, after, cancel }) => (
          <li key={leave.ItemId}>
            {t('LEAVE_DAYS_CHANGE_ITEM', {
              category: leave.LeaveCategory ?? '',
              from: formatDateOnly(leave.StartDate),
              to: formatDateOnly(leave.EndDate),
              before,
              after,
            })}
            {cancel && ` ${t('LEAVE_DAYS_CHANGE_CANCELLED')}`}
          </li>
        ))}
      </ul>
    </div>
  );
};

/** Every HR term, each a collapsible card with its period, working days, leave allotment and carry-forwards. */
const HrInfoTabForm = ({ employee }: { employee: Employee }) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  // Grown approved leaves are cancelled with the leave cancel (LEAVE_REQUEST_CANCEL), so only by its holders.
  const canCancelLeave = useHasPermission(HRM_PERMISSIONS.LEAVE.CANCEL);
  const resolver = useTranslatedResolver<HrInfoValues>(hrInfoSchema);
  const form = useForm<HrInfoValues>({
    resolver,
    defaultValues: employeeToHrInfoValues(employee),
  });
  useSyncLeaveDetails(form);
  const { mutateAsync, isPending } = useUpdateEmployeeHrInfo();
  // One empty term, so the fields inside the terms are marked too.
  const requiredFields = useMemo(
    () => requiredFieldsOf(hrInfoSchema, { HrTerms: [createEmptyTerm()], ActiveTermIndex: -1 }),
    []
  );

  // The open leaves the save recounts, while HR confirms it; `answer` settles the save waiting for them.
  const [leaveChanges, setLeaveChanges] = useState<LeaveDayChange[]>([]);
  const answer = useRef<(confirmed: boolean) => void>(() => undefined);
  const [isChecking, setIsChecking] = useState(false);

  /** Recounts the employee's open leaves with the new work days; when any changes, HR confirms first. */
  const confirmLeaveChanges = async (terms: ReturnType<typeof hrInfoValuesToTerms>) => {
    if (!employee.UserId) return { confirmed: true, changes: [] };
    setIsChecking(true);
    const changes = (
      await getLeaveDayChanges({
        userId: employee.UserId,
        before: employee.HrTerms,
        after: terms,
      }).finally(() => setIsChecking(false))
    ).map((change) => ({ ...change, cancel: change.cancel && canCancelLeave }));
    if (changes.length === 0) return { confirmed: true, changes };
    const confirmed = await new Promise<boolean>((resolve) => {
      answer.current = resolve;
      setLeaveChanges(changes);
    });
    setLeaveChanges([]);
    return { confirmed, changes };
  };

  /** An approved leave that now takes more days is cancelled (LEAVE_REQUEST_CANCEL: an overtime leave gets its
   * taken hours back, the employee is told). */
  const cancelGrownLeaves = async (changes: LeaveDayChange[]) => {
    const grown = changes.filter((change) => change.cancel);
    if (grown.length === 0) return;
    // One at a time: overtime leaves of the employee move the same overtime balance.
    const results = [];
    for (const change of grown) {
      results.push(
        await cancelLeaveRequest({
          itemId: change.leave.ItemId,
          note: t('LEAVE_WORK_DAYS_CHANGED_NOTE'),
        })
      );
    }
    await invalidateLeaveRequestQueries(queryClient);
    const failed = results.filter((result) => !result.IsSuccessful);
    toast(
      failed.length > 0
        ? {
            variant: 'destructive',
            title: t('LEAVE_DAYS_CANCEL_FAILED', { count: failed.length }),
            description: failed[0].Message,
          }
        : { title: t('LEAVE_DAYS_CANCELLED', { count: grown.length }) }
    );
  };

  const handleSave = async (values: HrInfoValues) => {
    const terms = hrInfoValuesToTerms(values);
    const { confirmed, changes } = await confirmLeaveChanges(terms);
    if (!confirmed) return false;
    const termIds = await mutateAsync({ employee, terms });
    // Terms inserted by this save keep their new id, so saving again updates them instead of
    // inserting them a second time (and they can no longer be removed).
    termIds.forEach((termId, index) => form.setValue(`HrTerms.${index}.ItemId`, termId));
    await cancelGrownLeaves(changes);
  };

  return (
    <>
      <TabForm
        form={form}
        onSave={handleSave}
        isSaving={isPending || isChecking || leaveChanges.length > 0}
        requiredFields={requiredFields}
      >
        <EmploymentStep />
      </TabForm>
      <ConfirmationModal
        open={leaveChanges.length > 0}
        onOpenChange={(open) => {
          if (!open) answer.current(false);
        }}
        title={t('LEAVE_DAYS_CHANGE_TITLE')}
        description={<LeaveDayChangeList changes={leaveChanges} />}
        onConfirm={() => answer.current(true)}
        confirmText={t('SAVE_CHANGES')}
        cancelText={t('CANCEL')}
      />
    </>
  );
};

const SkillsTabForm = ({
  employee,
  skillLookups,
}: {
  employee: Employee;
  skillLookups: SkillsFormLookups;
}) => {
  const resolver = useTranslatedResolver<SelfSkillsValues>(selfSkillsSchema);
  const form = useForm<SelfSkillsValues>({
    resolver,
    defaultValues: employeeToSkillsValues(employee, skillLookups),
  });
  const withCompletion = useCompletionFlag(employee);
  const { mutateAsync, isPending } = useUpdateMySkills();
  // One empty row of each list, so the fields inside the rows are marked too.
  const requiredFields = useMemo(
    () =>
      requiredFieldsOf(selfSkillsSchema, {
        CategoryId: '',
        SubCategoryExperiences: [createEmptySubCategoryExperience()],
        Skills: [createEmptySkillEntry()],
      }),
    []
  );

  const queryClient = useQueryClient();

  const handleSave = async (values: SelfSkillsValues) => {
    const picked = await queryClient.ensureQueryData(
      skillsForQueryOptions(
        savedSkillIdsOf(form.formState.defaultValues as SelfSkillsValues),
        values.SubCategoryExperiences.map((entry) => entry.SubCategoryId)
      )
    );
    await mutateAsync({
      employee,
      input: withCompletion(
        skillsValuesToInput(values, {
          ...skillLookups,
          skills: [...picked, ...skillLookups.skills],
        })
      ),
    });
  };

  return (
    <TabForm form={form} onSave={handleSave} isSaving={isPending} requiredFields={requiredFields}>
      <SkillsStep />
    </TabForm>
  );
};

/**
 * Editing a person, one tab per record: "Overview" (`UserProfileDetail`), "Contact Info"
 * (`UserContactDetail` with the personal details, plus the business email / phone) and "HR Info"
 * (`UserHRPeriodicalProfile`).
 * Every tab saves on its own; tabs stay mounted so switching keeps unsaved edits.
 */
export const EmployeeEditTabs = ({
  employee,
  mode = 'admin',
  skillLookups = { subCategories: [], skills: [] },
}: EmployeeEditTabsProps) => {
  const { t } = useTranslation();
  const isSelf = mode === 'self';
  const tabContentClass = 'mt-4 data-[state=inactive]:hidden';

  return (
    <Tabs defaultValue="overview">
      {/* Full width on a phone, each tab an equal share (scrolls if they don't fit). */}
      <TabsList className="w-full justify-start overflow-x-auto sm:w-auto">
        <TabsTrigger value="overview" className="max-sm:flex-1">
          {t('OVERVIEW')}
        </TabsTrigger>
        <TabsTrigger value="contact" className="max-sm:flex-1">
          {t('CONTACT_INFO')}
        </TabsTrigger>
        {isSelf ? (
          <TabsTrigger value="skills" className="max-sm:flex-1">
            {t('SKILLS')}
          </TabsTrigger>
        ) : (
          <TabsTrigger value="hr" className="max-sm:flex-1">
            {t('HR_INFO')}
          </TabsTrigger>
        )}
      </TabsList>
      <TabsContent value="overview" forceMount className={tabContentClass}>
        <OverviewTabForm employee={employee} isSelf={isSelf} />
      </TabsContent>
      <TabsContent value="contact" forceMount className={tabContentClass}>
        <ContactTabForm employee={employee} isSelf={isSelf} />
      </TabsContent>
      {isSelf ? (
        <TabsContent value="skills" forceMount className={tabContentClass}>
          <SkillsTabForm employee={employee} skillLookups={skillLookups} />
        </TabsContent>
      ) : (
        <TabsContent value="hr" forceMount className={tabContentClass}>
          <HrInfoTabForm employee={employee} />
        </TabsContent>
      )}
    </Tabs>
  );
};
